/**
 * sentry.ts — Centralized Sentry initialization.
 *
 * Call initSentry() once at app startup (main.tsx), before ReactDOM.createRoot().
 * The module is a no-op when VITE_SENTRY_DSN is absent (local dev, CI without secret).
 *
 * User context is set separately via setSentryUser() / clearSentryUser(),
 * called from AuthContext whenever the auth state changes.
 */

import * as Sentry from "@sentry/react";

// ── Errors that are noise, not bugs ──────────────────────────────────────────
// Keep this list short and evidence-based: only add entries that have been
// confirmed as non-actionable in production dashboards.
const IGNORED_ERRORS: Array<string | RegExp> = [
  // Browser quirk — fires when a ResizeObserver callback takes too long.
  // Not caused by our code; browsers differ on whether they throw at all.
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",

  // Generic network errors: user went offline, VPN, firewall, flaky mobile.
  // We can't fix these; they should not page on-call.
  /^Failed to fetch$/,
  /^Load failed$/,           // Safari equivalent of "Failed to fetch"
  /^NetworkError/,
  /^Network request failed/,
  "AbortError",

  // Vite/webpack chunk load failures — almost always a network blip after deploy.
  // The app auto-reloads on ChunkLoadError via Vite's built-in mechanism.
  /^ChunkLoadError:/,
  /Loading chunk \d+ failed/,
  /Loading CSS chunk \d+ failed/,
];

// ── Ignored URLs (third-party scripts we don't control) ──────────────────────
const IGNORED_URLS: RegExp[] = [
  /extensions\//i,         // Chrome extensions
  /^chrome:\/\//i,
  /^moz-extension:\/\//i,
];

// ─────────────────────────────────────────────────────────────────────────────

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // No DSN → silent no-op. This keeps local dev and preview branches clean.
  if (!dsn?.trim()) return;

  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT ??
    import.meta.env.MODE ??          // "production" | "development" | "staging"
    "unknown";

  Sentry.init({
    dsn,
    environment,

    // ── Integrations ────────────────────────────────────────────────────────
    integrations: [
      // Automatic page-load + navigation performance spans.
      // Uses the History API — works with React Router v6 BrowserRouter.
      Sentry.browserTracingIntegration(),
    ],

    // ── Sampling ────────────────────────────────────────────────────────────
    // 20 % performance traces — stays well inside the free 10 k txn/month quota.
    // Errors are always captured (not sampled by tracesSampleRate).
    tracesSampleRate: 0.2,

    // Only trace requests to our own backend — avoids polluting Supabase traces.
    tracePropagationTargets: [
      /^https:\/\/api\.callevo\.io/,
      /^http:\/\/localhost:8080/,
      /^http:\/\/127\.0\.0\.1:8080/,
    ],

    // ── Noise reduction ─────────────────────────────────────────────────────
    ignoreErrors: IGNORED_ERRORS,
    denyUrls: IGNORED_URLS,

    // ── beforeSend — final gate ──────────────────────────────────────────────
    beforeSend(event, hint) {
      // Drop everything from localhost so dev sessions never pollute Sentry.
      const host = window.location.hostname;
      if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
        return null;
      }

      // Drop unhandled promise rejections that are just cancelled fetches.
      // hint.originalException is the raw thrown value.
      const raw = hint?.originalException;
      if (raw instanceof DOMException && raw.name === "AbortError") return null;
      if (raw instanceof TypeError) {
        const msg = raw.message ?? "";
        if (/failed to fetch|load failed|networkerror/i.test(msg)) return null;
      }

      return event;
    },
  });
}

// ── User context helpers ──────────────────────────────────────────────────────

/**
 * Attach the authenticated user to all subsequent Sentry events.
 * Call this as soon as the Supabase session is resolved.
 *
 * We deliberately omit sensitive fields — only id + email for triage.
 */
export function setSentryUser(user: { id: string; email?: string | null }): void {
  Sentry.setUser({ id: user.id, email: user.email ?? undefined });
}

/** Clear user context on logout so events are no longer attributed. */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/** Re-export captureException for use outside React components. */
export { Sentry };
